// src/ui/components/speechBubble.ts — Socratic Speech Bubble (FR-AI-002, CON-003)
import { tokens } from '../designTokens';

export interface SpeechBubbleOptions {
  initialText?: string;
  maxSentences?: number; // Default 3 (CON-003)
}

export class SpeechBubble {
  private element: HTMLElement;
  private textElement: HTMLElement;
  private maxSentences: number;
  private typewriterInterval: number | null = null;

  constructor(options: SpeechBubbleOptions = {}) {
    this.maxSentences = options.maxSentences || 3;

    this.element = document.createElement('div');
    this.element.className = 'clawey-speech-bubble clawey-speech-card';
    this.element.style.background = 'radial-gradient(circle at center, #FFFDF9 50%, #F1E3C3 100%)';
    this.element.style.border = `2px solid ${tokens.color.gildedBrass}`;
    this.element.style.outline = '1.5px solid rgba(26, 54, 93, 0.25)';
    this.element.style.outlineOffset = '-5px';
    this.element.style.borderRadius = '14px 14px 14px 4px';
    this.element.style.boxShadow = '0 12px 28px rgba(4, 10, 22, 0.72), 0 0 16px rgba(0, 229, 255, 0.25)';
    this.element.style.padding = '14px 18px';
    this.element.style.position = 'relative';
    this.element.style.maxWidth = '360px';
    this.element.style.fontSize = '1.05rem';
    this.element.style.lineHeight = '1.5';
    this.element.style.color = tokens.color.morrisIndigo;
    this.element.style.fontWeight = '700';
    this.element.setAttribute('role', 'status');
    this.element.setAttribute('aria-live', 'polite');

    // Tail pointing towards mascot
    const tail = document.createElement('div');
    tail.className = 'bubble-tail';
    tail.style.position = 'absolute';
    tail.style.bottom = '-10px';
    tail.style.left = '24px';
    tail.style.width = '0';
    tail.style.height = '0';
    tail.style.borderLeft = '10px solid transparent';
    tail.style.borderRight = '10px solid transparent';
    tail.style.borderTop = `10px solid ${tokens.color.gildedBrass}`;
    this.element.appendChild(tail);

    this.textElement = document.createElement('div');
    this.textElement.className = 'bubble-text';
    this.element.appendChild(this.textElement);

    if (options.initialText) {
      this.setText(options.initialText, false);
    }
  }

  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Sanitizes and limits input text to satisfy CON-003 (≤3 sentences, zero negative framing).
   */
  public sanitizeText(rawText: string): string {
    let sanitized = rawText;

    // Replace negative framing with encouraging Socratic phrases (CON-003)
    const negativeReplacements: Array<[RegExp, string]> = [
      [/\b(that'?s\s+wrong|you'?re\s+wrong|wrong!|wrong\b)/gi, "A gallant try!"],
      [/\b(incorrect|that\s+is\s+not\s+correct)/gi, "An intriguing observation!"],
      [/\b(no,\s+|no\.\s+)/gi, "Ah, let us inspect further! "],
      [/\b(you\s+failed|failure)\b/gi, "Every tide brings new discoveries!"],
    ];

    for (const [pattern, replacement] of negativeReplacements) {
      sanitized = sanitized.replace(pattern, replacement);
    }

    // Split sentences by '.', '!', or '?'
    const sentences = sanitized.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || [sanitized];

    // Restrict strictly to <= maxSentences (FR-AI-002, CON-003)
    if (sentences.length > this.maxSentences) {
      sanitized = sentences.slice(0, this.maxSentences).join('').trim();
    }

    return sanitized.trim();
  }

  setText(text: string, typewriter = true, onComplete?: () => void): void {
    if (this.typewriterInterval !== null) {
      clearInterval(this.typewriterInterval);
      this.typewriterInterval = null;
    }

    const processedText = this.sanitizeText(text);

    if (!typewriter) {
      this.textElement.textContent = processedText;
      if (onComplete) onComplete();
      return;
    }

    this.textElement.textContent = '';
    let index = 0;
    this.typewriterInterval = window.setInterval(() => {
      if (index < processedText.length) {
        this.textElement.textContent += processedText.charAt(index);
        index++;
      } else {
        if (this.typewriterInterval !== null) {
          clearInterval(this.typewriterInterval);
          this.typewriterInterval = null;
        }
        if (onComplete) onComplete();
      }
    }, 20); // Fast, gentle typing speed
  }

  getText(): string {
    return this.textElement.textContent || '';
  }
}
